<script setup lang="ts">
import {
  ContractManagementContractFileType,
  PreprocessingTaskStatus,
  VaultFileRevisionKind,
  type ContractManagementContractFileInDb,
  type DocumentState,
  type ContractManagementContractInDb,
  type ContractManagementContractFileUpdateUser,
  type ContractManagementContractUpdate,
  type ContractUserShareInDb,
  type ContractRoleShareInDb,
  type ContractManagementMatterInDb,
  getContractContractualParties,
} from '@/api';
import {
  PARTY_KIND_LABELS,
  type ContractualParty,
} from '@/services/contractManagement/contractualPartyRow';
import {
  formatAccessPermissions,
  ACCESS_LEVELS,
  ACCESS_PERMISSION_ADMIN,
  ACCESS_PERMISSION_WRITE,
  deniedReason,
  gateOption,
} from '@/utils/accessPermission';
import { enumToSelectOptionsWithLabels } from '@/utils/enumUtils';
import { IconMore } from '@/components/design/icons';
import { contractFileTypeLabel } from '@/components/design/statusMaps';
import { gaiaGlyphPaths } from '@/components/design/glyphs';
import Layout from '@/components/design/layout/Layout.vue';
import GaiaAvatar from '@/components/design/base/GaiaAvatar.vue';
import GaiaAddButton from '@/components/design/base/GaiaAddButton.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaDeleteButton from '@/components/design/base/GaiaDeleteButton.vue';
import GaiaDrawer from '@/components/design/base/GaiaDrawer.vue';
import GaiaDropdown, { gaiaDangerOption } from '@/components/design/base/GaiaDropdown.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaPopover from '@/components/design/base/GaiaPopover.vue';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTextarea from '@/components/design/base/inputs/GaiaTextarea.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaTableSkeleton from '@/components/design/composite/GaiaTableSkeleton.vue';
import GaiaCheckMenu, {
  type GaiaCheckMenuOption,
} from '@/components/design/composite/GaiaCheckMenu.vue';
import GaiaDataTable, {
  type GaiaDataColumn,
} from '@/components/design/composite/GaiaDataTable.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaNotFound from '@/components/design/composite/GaiaNotFound.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import GaiaTabBar, { type GaiaTabBarItem } from '@/components/design/composite/GaiaTabBar.vue';
import GaiaPagination from '@/components/design/composite/GaiaPagination.vue';
import GaiaTableRowState from '@/components/design/composite/GaiaTableRowState.vue';
import {
  gaiaAccent,
  gaiaContentWidth,
  gaiaFonts,
  gaiaNeutral,
} from '@/components/design/themes/tokens';
import { ContractFileDownloadService } from '@/services/contractManagement/contractFileDownloadService';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useRowDelete } from '@/composables/useRowDelete';
import { type RouteLocationRaw, useRoute, useRouter } from 'vue-router';
import { askAiLimitWarning, askAiRoute } from '@/utils/askAi';
import { ContractFilesService } from '@/services/contractManagement/contractFilesService';
import { ContractsService } from '@/services/contractManagement/contractsService';
import { ContractShareService } from '@/services/contractShareService';
import ContractFileCreateModal from '@/components/contractManagement/ContractFileCreateModal.vue';
import MoveContractFileModal from '@/components/contractManagement/MoveContractFileModal.vue';
import SelectEntityModal from '@/components/contractManagement/SelectEntityModal.vue';
import MassShareContractsModal from '@/components/contractManagement/MassShareContractsModal.vue';
import VaultFileRevisionUploadModal from '@/components/file_vault/VaultFileRevisionUploadModal.vue';
import GaiaContractStateCell from '@/components/design/domain/contractManagement/GaiaContractStateCell.vue';
import { fileTypeHasState } from '@/components/design/domain/contractManagement/contractStateIcons';
import { useGaiaStore } from '@/stores/gaiaStore';
import { useIntegrationsStore } from '@/stores/integrationsStore';
import { useAuthStore } from '@/stores/auth';
import { useVaultStore } from '@/stores/vaultStore';
import { useContractTypeStore } from '@/stores/contractTypeStore';
import { isConversionRunning, isPdfFile } from '@/utils/fileTypeUtils';
import {
  documentStateFromMenuKey,
  documentStateMenuOption,
} from '@/components/file_vault/documentStateMenu';
import { useListQuerySync } from '@/composables/useListQuerySync';
import { logger } from '@/utils/logger';
import { extractErrorDetail, isAccessDenied } from '@/utils/errorHandler';
import GaiaVaultCrumbs from '@/components/design/domain/fileVault/GaiaVaultCrumbs.vue';
import GaiaVaultFilePdfPreview from '@/components/design/domain/fileVault/GaiaVaultFilePdfPreview.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';
import {
  contractFileOpenRoute,
  contractRoute,
  contractualPartyRoute,
  vaultDirectoryRoute,
} from '@/router/links';
import { contractFolderRoute } from '@/services/vaultLocationService';

// ==================== Router ====================
const route = useRoute();
const router = useRouter();

const contractUid = computed(() => {
  // New route uses :uid param
  if (route.params.uid) {
    return route.params.uid as string;
  }
  // Old route uses :contractUid param
  return route.params.contractUid as string;
});

// ==================== Local State ====================
const contractFiles = ref<ContractManagementContractFileInDb[]>([]);
const contract = ref<ContractManagementContractInDb | null>(null);
/** The backend refused the contract: deleted, no access, or another organisation's. */
const unavailable = ref(false);
const loading = ref<boolean>(false);
const totalCount = ref<number>(0);
const loadingContract = ref<boolean>(false);
const matters = ref<ContractManagementMatterInDb[]>([]);
const loadingMatters = ref<boolean>(false);
/** Read-only here: parties link to the contract's files, so they're managed per file. */
const parties = ref<ContractualParty[]>([]);
const loadingParties = ref<boolean>(false);
type PanelTab = 'matters' | 'parties' | 'shared-users' | 'shared-roles';

const rightPanelTab = ref<PanelTab>('matters');
const sharedUserList = ref<ContractUserShareInDb[]>([]);
const sharedRoleList = ref<ContractRoleShareInDb[]>([]);
const loadingSharedUsers = ref<boolean>(false);
const loadingSharedRoles = ref<boolean>(false);
const removingShareUserUid = ref<string | null>(null);
const removingShareRoleUid = ref<string | null>(null);

// Edit share permission popover (bitmask)
const editingShareUserUid = ref<string | null>(null);
const editingShareRoleUid = ref<string | null>(null);
const editPermissionBitmask = ref<number>(0);
const savingSharePermission = ref(false);

const message = useGaiaToast();

function handleAskAi(): void {
  const contractUids = [contractUid.value].filter(Boolean);
  const limitWarning = askAiLimitWarning({ contractUids });
  if (limitWarning) {
    message.warning(limitWarning);
    return;
  }
  void router.push(askAiRoute({ contractUids }));
}
const gaiaStore = useGaiaStore();
const integrationsStore = useIntegrationsStore();
const authStore = useAuthStore();
const vaultStore = useVaultStore();
const contractTypeStore = useContractTypeStore();
const { currentPage, pageSize, searchQuery, sortKey, sortDirection, sortActivated } =
  useListQuerySync();
const showCreateModal = ref<boolean>(false);
const downloadingFileUid = ref<string | null>(null);
const removingUid = ref<string | null>(null);
const showAddMatterModal = ref<boolean>(false);

// Edit/Delete state for contract files
const showEditModal = ref<boolean>(false);
const editingContractFile = ref<ContractManagementContractFileInDb | null>(null);
const editFormData = ref<{
  /** Null until the AI has classified the file. */
  type: ContractManagementContractFileType | null;
  description: string;
}>({
  type: null,
  description: '',
});
const saving = ref<boolean>(false);
const showMoveModal = ref(false);
const contractFileToMoveUid = ref<string | null>(null);
const showShareModal = ref(false);
const showRevisionUploadModal = ref(false);
const revisionUploadFileUid = ref('');
const revisionUploadFileName = ref('');
const showGaiaSectionModal = ref(false);
const gaiaExportVaultFileUid = ref<string | null>(null);
const gaiaExportFileName = ref('');
const selectedGaiaSections = ref<string[]>([]);
const isExportingToGaia = ref(false);
const gaiaExportError = ref<string | null>(null);

// Edit/Delete state for contract
const showEditContractModal = ref<boolean>(false);
const contractEditFormData = ref<{
  name: string;
  contractTypeUid: string | null;
  description: string;
  tags: string[];
}>({
  name: '',
  contractTypeUid: null,
  description: '',
  tags: [],
});
const savingContract = ref<boolean>(false);
const deletingContract = ref<boolean>(false);
/** The conversion awaiting confirmation; null while none is asked. */
const pendingConversion = ref<'file' | 'folder' | null>(null);
const convertingContract = ref<boolean>(false);
const repopulating = ref<boolean>(false);
const tagsInput = ref<string>('');

// --- row + header confirm state (the mock's inline strips) -----------------
/** The file row currently asking to be deleted; one at a time. */
const rowDelete = useRowDelete<ContractManagementContractFileInDb>();
/** Header confirms act on the whole record, so they stay small dialogs. */
const confirmRepopulate = ref<boolean>(false);
const confirmDeleteContract = ref<boolean>(false);
const contractNameError = ref<string | null>(null);
/** Set when the file list itself fails, so the card can own that error. */
const filesError = ref<string | null>(null);

// ==================== Computed Pagination Config ====================

const gaiaSectionOptions = computed(() => {
  return gaiaStore.sectionGroups.data
    .map(group => ({
      type: 'group',
      label: group.title,
      key: group.id || group.title,
      children: group.sections
        .filter(section => section.id && section.title)
        .map(section => ({
          label: section.title,
          value: section.id,
        })),
    }))
    .filter(group => group.children.length > 0);
});

// ==================== Data Fetching ====================
async function fetchContract(): Promise<void> {
  loadingContract.value = true;
  unavailable.value = false;
  try {
    const contractData = await ContractsService.getById(contractUid.value);
    contract.value = contractData;
    // Update matters from contract data
    matters.value = contractData.matters;
  } catch (error) {
    if (isAccessDenied(error)) {
      // An answer, not a failure: the page renders "not available" instead.
      logger.warn('Contract not available:', error);
      unavailable.value = true;
      return;
    }
    logger.error('Error fetching contract:', error);
    message.error('Failed to fetch contract information');
  } finally {
    loadingContract.value = false;
  }
}

async function fetchParties(): Promise<void> {
  loadingParties.value = true;
  try {
    const { data } = await getContractContractualParties({
      path: { uid: contractUid.value },
      throwOnError: true,
    });
    parties.value = data;
  } catch (error) {
    logger.error('Error fetching contractual parties:', error);
    message.error('Failed to fetch contractual parties');
  } finally {
    loadingParties.value = false;
  }
}

function partyRoute(partyUid: string): RouteLocationRaw {
  return contractualPartyRoute(partyUid);
}

// Refresh matters
async function refreshMatters(): Promise<void> {
  // Re-fetch contract to get updated matters
  await fetchContract();
}

// ==================== Shared users / roles ====================
async function loadSharedUsers(): Promise<void> {
  if (!contractUid.value) return;
  loadingSharedUsers.value = true;
  try {
    sharedUserList.value = await ContractShareService.getUserSharesByContract(contractUid.value);
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to load shared users');
    sharedUserList.value = [];
  } finally {
    loadingSharedUsers.value = false;
  }
}

async function loadSharedRoles(): Promise<void> {
  if (!contractUid.value) return;
  loadingSharedRoles.value = true;
  try {
    sharedRoleList.value = await ContractShareService.getRoleSharesByContract(contractUid.value);
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to load shared roles');
    sharedRoleList.value = [];
  } finally {
    loadingSharedRoles.value = false;
  }
}

/** Display combined permissions (bitmask) e.g. "Read, Write" */
function permissionDisplay(permissions: number): string {
  return formatAccessPermissions(permissions);
}

async function handleRemoveSharedUser(userUid: string): Promise<void> {
  if (!contractUid.value || removingShareUserUid.value) return;
  removingShareUserUid.value = userUid;
  try {
    await ContractShareService.removeUserShare(contractUid.value, userUid);
    message.success('User removed from share');
    await loadSharedUsers();
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to remove share');
  } finally {
    removingShareUserUid.value = null;
  }
}

async function handleRemoveSharedRole(roleUid: string): Promise<void> {
  if (!contractUid.value || removingShareRoleUid.value) return;
  removingShareRoleUid.value = roleUid;
  try {
    await ContractShareService.removeRoleShare(contractUid.value, roleUid);
    message.success('Role removed from share');
    await loadSharedRoles();
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to remove share');
  } finally {
    removingShareRoleUid.value = null;
  }
}

async function handleSharedFromModal(): Promise<void> {
  await Promise.all([loadSharedUsers(), loadSharedRoles()]);
}

function openEditUserSharePopover(share: ContractUserShareInDb): void {
  editingShareUserUid.value = share.user.uid;
  editingShareRoleUid.value = null;
  editPermissionBitmask.value = share.permissions;
}

function openEditRoleSharePopover(share: ContractRoleShareInDb): void {
  editingShareRoleUid.value = share.role.uid;
  editingShareUserUid.value = null;
  editPermissionBitmask.value = share.permissions;
}

/** The access levels as the menu wants them — key is the level's mask, as a string. */
const editPermissionOptions = computed<GaiaCheckMenuOption[]>(() =>
  ACCESS_LEVELS.map(level => ({
    key: String(level.value),
    label: level.label,
    checked: editPermissionBitmask.value === level.value,
  }))
);

function toggleEditPermissionFlag(key: string): void {
  editPermissionBitmask.value = Number(key);
}

function closeEditSharePopover(): void {
  editingShareUserUid.value = null;
  editingShareRoleUid.value = null;
}

async function saveEditSharePermission(): Promise<void> {
  const contract = contractUid.value;
  if (!contract || savingSharePermission.value) return;
  if (editPermissionBitmask.value === 0) {
    message.warning('Choose an access level');
    return;
  }
  savingSharePermission.value = true;
  try {
    if (editingShareUserUid.value) {
      await ContractShareService.updateUserSharePermissions(
        contract,
        editingShareUserUid.value,
        editPermissionBitmask.value
      );
      message.success('Access updated');
      await loadSharedUsers();
    } else if (editingShareRoleUid.value) {
      await ContractShareService.updateRoleSharePermissions(
        contract,
        editingShareRoleUid.value,
        editPermissionBitmask.value
      );
      message.success('Access updated');
      await loadSharedRoles();
    }
    closeEditSharePopover();
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to update permissions');
  } finally {
    savingSharePermission.value = false;
  }
}

watch(rightPanelTab, tab => {
  if (tab === 'shared-users' && sharedUserList.value.length === 0 && contractUid.value) {
    void loadSharedUsers();
  }
  if (tab === 'shared-roles' && sharedRoleList.value.length === 0 && contractUid.value) {
    void loadSharedRoles();
  }
});

watch(contractUid, uid => {
  if (uid && rightPanelTab.value === 'shared-users') void loadSharedUsers();
  if (uid && rightPanelTab.value === 'shared-roles') void loadSharedRoles();
});

// Handle adding matter
function handleAddMatter(): void {
  showAddMatterModal.value = true;
}

/**
 * Why the contract can't go out for signing; null when it can. A draft is
 * rendered on view and never gets the stored preview signing needs, so the
 * backend refuses it until it is finished.
 */
const onlyDrafts = computed(
  () =>
    contractFiles.value.length > 0 &&
    contractFiles.value.every(
      file => file.vault_file?.current_revision?.kind === VaultFileRevisionKind.TEMPLATED
    )
);
const signBlock = computed<string | null>(() => {
  if (writeBlock.value) return writeBlock.value;
  return onlyDrafts.value ? 'Finish the draft before sending it for signing.' : null;
});

/** Disables a header option while the contract holds nothing but a draft. */
function draftBlocked(option: Record<string, unknown>, reason: string): Record<string, unknown> {
  return onlyDrafts.value ? { ...option, disabled: true, props: { title: reason } } : option;
}

function handleSignThis(): void {
  void router.push({
    name: 'signing-contract-request-new',
    params: { uid: contractUid.value },
    query: {
      contract_uid: contractUid.value,
    },
  });
}

// Handle matter assigned from modal
async function handleMatterAssigned(matterUid: string): Promise<void> {
  try {
    await ContractsService.addMatterToContract(matterUid, contractUid.value);
    message.success('Matter assigned successfully');
    await refreshMatters();
  } catch (error) {
    logger.error('Error assigning matter:', error);
    const errorMsg =
      error instanceof Error ? error.message : 'Failed to assign matter. Please try again.';
    message.error(errorMsg);
  }
}

// Handle matter click - navigate to matter detail
function handleMatterClick(matterUid: string, e: MouseEvent): void {
  const target = e.target as HTMLElement;
  if (
    target.closest('button') ||
    target.closest('.n-popconfirm') ||
    target.closest('.matter-unlink-btn')
  ) {
    return;
  }

  void router.push(matterRoute(matterUid));
}

function matterRoute(matterUid: string): RouteLocationRaw {
  return { name: 'contract-management-detail', params: { entityType: 'matter', uid: matterUid } };
}

// Handle unlink matter from contract
async function handleUnlinkMatter(matterUid: string, matterName: string): Promise<void> {
  removingUid.value = matterUid;
  try {
    await ContractsService.removeMatterFromContract(matterUid, contractUid.value);
    message.success(`${matterName} unlinked successfully`);
    await refreshMatters();
  } catch (error) {
    logger.error('Error unlinking matter:', error);
    const errorMsg =
      error instanceof Error ? error.message : 'Failed to unlink matter. Please try again.';
    message.error(errorMsg);
  } finally {
    removingUid.value = null;
  }
}

async function fetchContractFiles(): Promise<void> {
  loading.value = true;

  try {
    const result = await ContractFilesService.getContractFilesForContract(contractUid.value, {
      text: searchQuery.value || '',
      page: currentPage.value,
      pageSize: pageSize.value,
      sortKey: sortKey.value,
      sortDirection: sortDirection.value,
      columnFilters: {},
    });

    contractFiles.value = result.results;
    totalCount.value = result.result_count;
  } catch (error) {
    logger.error('Error fetching contract files:', error);
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch contract files';
    message.error(errorMessage);
  } finally {
    loading.value = false;
  }
}

// ==================== Sort Handling ====================

// ==================== Watch for Page Changes ====================
watch(currentPage, () => {
  if (currentPage.value > 0) {
    void fetchContractFiles();
  }
});

// ==================== Watch for Page Size Changes ====================
watch(pageSize, () => {
  if (pageSize.value > 0) {
    void fetchContractFiles();
  }
});

// A new search starts from the first page and drops any manual sort. The field
// debounces its own keystrokes and fires this at once when it is cleared.
async function applySearch(): Promise<void> {
  currentPage.value = 1;
  sortActivated.value = false;
  await fetchContractFiles();
}

// ==================== Date Formatting ====================
function formatDate(dateString: string | null): string {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ==================== Download Functionality ====================
async function downloadContractFile(file: ContractManagementContractFileInDb): Promise<void> {
  try {
    downloadingFileUid.value = file.uid;
    const result = await ContractFileDownloadService.downloadContractFile(
      file.uid,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to undefined
      file.description || undefined
    );
    if (result.success) {
      message.success('File downloaded successfully');
    } else {
      if (result.error?.includes('does not have an associated vault file')) {
        message.warning(result.error || 'File download is not available for this contract file.');
      } else {
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        message.error(result.error || 'Failed to download file');
      }
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to download file';
    message.error(errorMessage);
  } finally {
    downloadingFileUid.value = null;
  }
}

async function openGaiaSectionModal(
  row: ContractManagementContractFileInDb,
  e?: MouseEvent
): Promise<void> {
  e?.stopPropagation();
  const vaultFileUid = row.vault_file?.uid;
  if (!vaultFileUid) {
    message.warning('This contract file has no vault file to export.');
    return;
  }

  if (!integrationsStore.activeIntegrations.gaia) {
    message.error('GAIA integration is not available.');
    return;
  }

  gaiaExportVaultFileUid.value = vaultFileUid;
  gaiaExportFileName.value =
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand; empty string must use the fallback
    row.vault_file?.current_revision?.file_name || row.description || `File ${row.uid.slice(0, 8)}`;
  selectedGaiaSections.value = [];
  gaiaExportError.value = null;
  showGaiaSectionModal.value = true;

  try {
    await gaiaStore.fetchSectionGroups('write');
  } catch (error) {
    logger.error('Failed to load Gaia sections:', error);
    gaiaExportError.value = 'Failed to load Gaia CLM sections. Please try again.';
  }
}

async function exportContractFileToGaiaSections(): Promise<void> {
  const vaultFileUid = gaiaExportVaultFileUid.value;
  if (!vaultFileUid || selectedGaiaSections.value.length === 0) return;

  isExportingToGaia.value = true;
  gaiaExportError.value = null;

  try {
    await Promise.all(
      selectedGaiaSections.value.map(sectionId =>
        gaiaStore.exportVaultFileToGaia(vaultFileUid, sectionId)
      )
    );

    message.success(
      `Successfully exported ${gaiaExportFileName.value} to ${selectedGaiaSections.value.length} section(s)`
    );
    closeGaiaSectionModal();
  } catch (error) {
    logger.error('Failed to export contract file to Gaia:', error);
    gaiaExportError.value = 'Failed to export file to Gaia CLM sections. Please try again.';
    message.error('Failed to export file to Gaia CLM sections');
  } finally {
    isExportingToGaia.value = false;
  }
}

function closeGaiaSectionModal(): void {
  showGaiaSectionModal.value = false;
  gaiaExportVaultFileUid.value = null;
  gaiaExportFileName.value = '';
  selectedGaiaSections.value = [];
  gaiaExportError.value = null;
}

const showPreview = ref(false);
const previewFileUid = ref<string | null>(null);

// Handle contract file row click - its detail view, or the wizard for a draft
function contractFileRoute(file: ContractManagementContractFileInDb): RouteLocationRaw {
  return contractFileOpenRoute(file);
}

// ==================== Presentation helpers ====================
/**
 * The mock's file row: a display name, a kind chip, signatures, a size and two
 * dates. Everything here is derivation for the template — the fetching and the
 * mutations above are untouched.
 */
function fileDisplayName(file: ContractManagementContractFileInDb): string {
  return (
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
    file.vault_file?.current_revision?.file_name ||
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
    file.description ||
    `File ${file.uid.slice(0, 8)}`
  );
}

/** Bytes at one decimal; the fraction is dropped once the number is big enough. */
function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unit]}`;
}

function fileSize(file: ContractManagementContractFileInDb): string {
  return formatBytes(file.vault_file?.current_revision?.size_b);
}

/** Column tracks for the files card — the mock's own widths. */
const fileColumns: GaiaDataColumn[] = [
  { key: 'name', label: 'File', width: 'minmax(280px,1.7fr)' },
  { key: 'type', label: 'Kind', width: '110px' },
  { key: 'state', label: 'Status', width: '200px' },
  { key: 'size', label: 'Size', width: '92px' },
  { key: 'created_at', label: 'Created', width: '150px', sortable: true },
  { key: 'updated_at', label: 'Updated', width: '150px' },
];

function onFilesSort(key: string): void {
  if (sortActivated.value && sortKey.value === key) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortActivated.value = true;
    sortKey.value = key;
    sortDirection.value = 'desc';
  }
  currentPage.value = 1;
  void fetchContractFiles();
}

// ==================== Permission bits ====================

/** The caller's bits on this contract; null (not computed) gates nothing. */
const contractBits = computed(() => contract.value?.permissions);

/** Why the caller can't do what needs `flag` on the contract; null when they can. */
const writeBlock = computed(() => deniedReason(contractBits.value, ACCESS_PERMISSION_WRITE));
const adminBlock = computed(() => deniedReason(contractBits.value, ACCESS_PERMISSION_ADMIN));

/**
 * The per-file ⋯ menu. Built per row because three entries only apply when the
 * file actually has a vault file behind it. A contract file carries its
 * contract's bits.
 */
function fileMenuOptions(file: ContractManagementContractFileInDb): Record<string, unknown>[] {
  const vaultFileUid = file.vault_file?.uid;
  const revision = file.vault_file?.current_revision;
  const bits = file.vault_file?.permissions ?? contractBits.value;
  const items: Record<string, unknown>[] = [
    revision?.kind === VaultFileRevisionKind.TEMPLATED
      ? { key: 'open', label: 'Continue draft' }
      : { key: 'open', label: 'Open file' },
    { key: 'download', label: 'Download file' },
  ];
  if (vaultFileUid) {
    // The file as it reads now, without leaving the contract — a draft opens
    // in the wizard, so this is where it can just be looked at.
    const ready = revision?.preview_status === PreprocessingTaskStatus.DONE;
    items.splice(1, 0, {
      key: 'preview',
      label: ready ? 'Preview' : 'Preview not ready',
      disabled: !ready,
    });
  }
  if (vaultFileUid) {
    items.push(
      gateOption({ key: 'add-revision', label: 'Add revision' }, bits, ACCESS_PERMISSION_WRITE)
    );
    if (revision) {
      // A draft revision is always "In draft": the backend refuses a change.
      if (fileTypeHasState(file.type) && revision.kind !== VaultFileRevisionKind.TEMPLATED) {
        items.push(
          gateOption(
            documentStateMenuOption(revision.document_state),
            bits,
            ACCESS_PERMISSION_WRITE
          )
        );
      }
      // As in the vault: PDFs only, held while a conversion runs.
      if (isPdfFile(revision.mime_type, revision.file_name)) {
        const converting = isConversionRunning(revision.conversion_status);
        items.push(
          gateOption(
            {
              key: 'convert-to-docx',
              label: converting ? 'Conversion in progress' : 'Convert to DOCX',
              disabled: converting,
            },
            bits,
            ACCESS_PERMISSION_WRITE
          )
        );
      }
    }
    if (integrationsStore.activeIntegrations.gaia) {
      items.push({ key: 'export-gaia', label: 'Export to Gaia CLM Section' });
    }
  }
  items.push(
    gateOption({ key: 'move', label: 'Move to another contract' }, bits, ACCESS_PERMISSION_ADMIN),
    gateOption({ key: 'edit', label: 'Edit details' }, bits, ACCESS_PERMISSION_WRITE),
    { key: 'divider', type: 'divider' },
    gateOption(
      { key: 'delete', label: 'Delete file', props: gaiaDangerOption },
      bits,
      ACCESS_PERMISSION_ADMIN
    )
  );
  return items;
}

function onFileMenu(key: string, file: ContractManagementContractFileInDb): void {
  const documentState = documentStateFromMenuKey(key);
  if (documentState !== undefined) {
    void setFileDocumentState(file, documentState);
    return;
  }
  switch (key) {
    case 'open':
      void router.push(contractFileRoute(file));
      return;
    case 'preview':
      previewFileUid.value = file.vault_file?.uid ?? null;
      showPreview.value = previewFileUid.value !== null;
      return;
    case 'download':
      void downloadContractFile(file);
      return;
    case 'add-revision':
      revisionUploadFileUid.value = file.vault_file?.uid ?? '';
      revisionUploadFileName.value = file.vault_file?.current_revision?.file_name ?? '';
      showRevisionUploadModal.value = true;
      return;
    case 'convert-to-docx':
      void convertFileToDocx(file);
      return;
    case 'export-gaia':
      void openGaiaSectionModal(file);
      return;
    case 'move':
      handleMove(file);
      return;
    case 'edit':
      openFileEdit(file);
      return;
    case 'delete':
      rowDelete.arm(file);
  }
}

/**
 * Sets the lifecycle state of the file's current revision, then refetches the
 * files and — quietly, without the page's loading state — the contract, whose
 * header state is derived from them.
 */
async function setFileDocumentState(
  file: ContractManagementContractFileInDb,
  state: DocumentState
): Promise<void> {
  const vaultFile = file.vault_file;
  const revisionUid = vaultFile?.current_revision?.uid;
  if (!vaultFile || !revisionUid) return;
  try {
    await vaultStore.updateRevision(vaultFile.uid, revisionUid, { document_state: state });
  } catch (error) {
    logger.error('Failed to update the document state:', error);
    // The backend refuses some transitions (422) and says why.
    message.error(extractErrorDetail(error, 'Failed to update the state'));
    return;
  }
  await fetchContractFiles();
  try {
    contract.value = await ContractsService.getById(contractUid.value);
  } catch (error) {
    logger.error('Failed to refresh the contract state:', error);
  }
}

/** Starts the file's PDF → DOCX conversion; the refetch shows it as in progress. */
async function convertFileToDocx(file: ContractManagementContractFileInDb): Promise<void> {
  const vaultFileUid = file.vault_file?.uid;
  if (!vaultFileUid) return;
  try {
    const result = await vaultStore.convertPdfToDocx(vaultFileUid);
    message.success(result.message || `Started converting ${fileDisplayName(file)} to DOCX`);
    await fetchContractFiles();
  } catch (error) {
    logger.error('Failed to convert file to DOCX:', error);
    message.error(
      `Failed to convert file to DOCX: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

/** Delete confirmed from the row strip, with the failure kept on the row. */
async function deleteFileConfirmed(file: ContractManagementContractFileInDb): Promise<void> {
  // The last file takes the contract with it; resolve where to land while the
  // contract still has a trail.
  const lastFile = totalCount.value === 1;
  const landing = lastFile ? await contractFolderRoute('contract', contractUid.value) : null;
  await rowDelete.run(file, {
    work: async () => {
      await ContractFilesService.delete(file.uid);
      if (landing) {
        message.success('File and contract deleted');
        await router.push(landing);
        return;
      }
      message.success('Contract file deleted');
      await fetchContractFiles();
    },
    errorText: () => 'Couldn’t delete this file. Nothing was changed.',
  });
}

/** The header ⋯ menu — everything that isn't Sign or Share. */
const headerMenuOptions = computed((): Record<string, unknown>[] => {
  const bits = contractBits.value;
  const write = (option: Record<string, unknown>): Record<string, unknown> =>
    gateOption(option, bits, ACCESS_PERMISSION_WRITE);
  const admin = (option: Record<string, unknown>): Record<string, unknown> =>
    gateOption(option, bits, ACCESS_PERMISSION_ADMIN);
  return [
    draftBlocked(
      { key: 'ask-ai', label: 'Ask AI' },
      'A draft has no text for the AI to read until it is saved.'
    ),
    { key: 'assign-matter', label: 'Assign matter' },
    write({ key: 'edit', label: 'Edit contract' }),
    write({ key: 'repopulate', label: 'Re-run extraction' }),
    { key: 'divider-convert', type: 'divider' },
    // Only a contract with exactly one file can become a file.
    ...(totalCount.value === 1
      ? [
          // A loose draft can't be filed as a contract again: it never gets the
          // text extraction converting waits for.
          draftBlocked(
            admin({ key: 'convert-to-file', label: 'Convert to file' }),
            'Save the draft before converting it to a file.'
          ),
        ]
      : []),
    admin({ key: 'convert-to-folder', label: 'Convert to folder' }),
    { key: 'divider', type: 'divider' },
    admin({ key: 'delete', label: 'Delete contract', props: gaiaDangerOption }),
  ];
});

function onHeaderMenu(key: string): void {
  switch (key) {
    case 'ask-ai':
      handleAskAi();
      return;
    case 'share':
      openShare();
      return;
    case 'assign-matter':
      handleAddMatter();
      return;
    case 'edit':
      handleEditContract();
      return;
    case 'repopulate':
      confirmRepopulate.value = true;
      return;
    case 'convert-to-file':
      pendingConversion.value = 'file';
      return;
    case 'convert-to-folder':
      pendingConversion.value = 'folder';
      return;
    case 'delete':
      confirmDeleteContract.value = true;
  }
}

/** The left half of the split card, as the mock's label/value list. */
interface DetailField {
  label: string;
  kind: 'text' | 'tags' | 'owner';
  value?: string;
  tags?: string[];
  muted?: boolean;
}

const detailFields = computed((): DetailField[] => {
  const fields: DetailField[] = [
    { label: 'Type', kind: 'text', value: contractType.value },
    { label: 'Owner', kind: 'owner' },
  ];
  fields.push(
    { label: 'Tags', kind: 'tags', tags: contract.value?.tags ?? [] },
    { label: 'Created', kind: 'text', value: contractCreatedAt.value },
    { label: 'Updated', kind: 'text', value: contractUpdatedAt.value },
    {
      label: 'Description',
      kind: 'text',
      value: contractDescription.value,
      muted: !contract.value?.description,
    }
  );
  return fields;
});

const panelTabs = computed<GaiaTabBarItem[]>(() => [
  { key: 'matters', label: 'Matters', count: matters.value.length },
  { key: 'parties', label: 'Parties', count: parties.value.length },
  { key: 'shared-users', label: 'Shared users', count: sharedUserList.value.length },
  { key: 'shared-roles', label: 'Shared roles', count: sharedRoleList.value.length },
]);

/** People and roles share one picker, so the dialog has no tab to open on. */
function openShare(): void {
  showShareModal.value = true;
}

/** Sharing needs ADMIN on the contract; the matters tab is not gated. */
const panelAddBlock = computed(() => (rightPanelTab.value === 'matters' ? null : adminBlock.value));

/** What the "＋" in the panel head does, per tab. */
function onPanelAdd(): void {
  if (rightPanelTab.value === 'matters') {
    handleAddMatter();
    return;
  }
  openShare();
}

const panelEmptyCopy = computed(() => {
  switch (rightPanelTab.value) {
    case 'matters':
      return {
        title: 'No matters linked',
        body: 'Link a matter so this contract shows up in that matter’s file.',
        cta: 'Assign matter',
      };
    case 'parties':
      return {
        title: 'No parties linked',
        body: 'Parties are linked on each contract file — open a file to add one.',
        cta: '',
      };
    case 'shared-users':
      return {
        title: 'Not shared with anyone',
        body: 'Share this contract to let a colleague open it without opening the whole folder.',
        cta: 'Share',
      };
    default:
      return {
        title: 'No roles have access',
        body: 'Share with a role to grant everyone who holds it the same access.',
        cta: 'Share',
      };
  }
});

const panelLoading = computed(() => {
  if (rightPanelTab.value === 'matters') return loadingMatters.value;
  if (rightPanelTab.value === 'parties') return loadingParties.value;
  if (rightPanelTab.value === 'shared-users') return loadingSharedUsers.value;
  return loadingSharedRoles.value;
});

const panelIsEmpty = computed(() => {
  if (panelLoading.value) return false;
  if (rightPanelTab.value === 'matters') return matters.value.length === 0;
  if (rightPanelTab.value === 'parties') return parties.value.length === 0;
  if (rightPanelTab.value === 'shared-users') return sharedUserList.value.length === 0;
  return sharedRoleList.value.length === 0;
});

function confirmRepopulateNow(): void {
  confirmRepopulate.value = false;
  void handleRepopulate();
}

function confirmDeleteNow(): void {
  confirmDeleteContract.value = false;
  void handleDeleteContract();
}

/** Two letters for the share/matter avatars. */
function initialsOf(text: string | null | undefined): string {
  const value = (text ?? '').trim();
  if (!value) return '?';
  const parts = value.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return value.slice(0, 2).toUpperCase();
}

// ==================== Options for Selects ====================
// Generate options from enums with automatic label generation
// Labels are automatically converted from snake_case to Title Case
// No hardcoded label maps needed - labels are generated automatically from enum values
const fileTypeOptions = enumToSelectOptionsWithLabels(ContractManagementContractFileType);

/** `handleEdit` takes a MouseEvent only to stop propagation; the menu has none. */
function openFileEdit(file: ContractManagementContractFileInDb): void {
  editingContractFile.value = file;
  editFormData.value = { type: file.type, description: file.description ?? '' };
  showEditModal.value = true;
}

// ==================== Edit/Delete/Move Handlers ====================

function handleMove(contractFile: ContractManagementContractFileInDb, e?: MouseEvent): void {
  e?.stopPropagation();
  contractFileToMoveUid.value = contractFile.uid;
  showMoveModal.value = true;
}

async function handleMoved(
  _contractFileUid: string,
  _updatedContractFile: ContractManagementContractFileInDb,
  targetContract?: ContractManagementContractInDb
): Promise<void> {
  const hasOnlyOneFile = contractFiles.value.length === 1;
  const targetUid = targetContract?.uid;

  contractFileToMoveUid.value = null;
  showMoveModal.value = false;

  // Backend deletes the contract when its last file is moved. Navigate to the target contract
  // so we don't refetch a contract that no longer exists.
  if (hasOnlyOneFile && targetUid) {
    await nextTick();
    await router.replace(contractRoute(targetUid));
    return;
  }

  await fetchContractFiles();
}

watch(showMoveModal, visible => {
  if (!visible) contractFileToMoveUid.value = null;
});

async function handleSaveEdit(): Promise<void> {
  if (!editingContractFile.value) return;

  saving.value = true;
  try {
    const updateData: ContractManagementContractFileUpdateUser = {
      // A PUT cannot clear the type; while unclassified it is left out.
      type: editFormData.value.type ?? undefined,
      description: editFormData.value.description.trim() || null,
    };

    await ContractFilesService.update(editingContractFile.value.uid, updateData);
    message.success('Contract file updated successfully');
    showEditModal.value = false;
    await fetchContractFiles();
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : 'Failed to update contract file';
    message.error(errorMsg);
  } finally {
    saving.value = false;
  }
}

function handleCancelEdit(): void {
  showEditModal.value = false;
  editingContractFile.value = null;
  editFormData.value = { type: null, description: '' };
}

// ==================== Create Contract File ====================
function handleCreateClick(): void {
  showCreateModal.value = true;
}

async function handleContractFileCreated(): Promise<void> {
  // Refresh the contract files list
  await fetchContractFiles();
}

// ==================== Computed Properties ====================
// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
const contractName = computed(() => contract.value?.name || '—');

const contractType = computed(() => contract.value?.contract_type?.name ?? '—');
// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
const contractDescription = computed(() => contract.value?.description || '—');
const contractCreatedAt = computed(() => {
  if (!contract.value?.created_at) return '—';
  return formatDate(contract.value.created_at);
});
const contractUpdatedAt = computed(() => {
  if (!contract.value?.updated_at) return '—';
  return formatDate(contract.value.updated_at);
});

// ==================== Contract Edit/Delete Handlers ====================
function handleEditContract(): void {
  if (!contract.value) return;
  void contractTypeStore.ensureLoaded();
  contractEditFormData.value = {
    name: contract.value.name,
    contractTypeUid: contract.value.contract_type?.uid ?? null,
    description: contract.value.description ?? '',
    tags: contract.value.tags,
  };
  tagsInput.value = contract.value.tags.length > 0 ? contract.value.tags.join(', ') : '';
  showEditContractModal.value = true;
}

async function handleSaveContractEdit(): Promise<void> {
  if (!contract.value) return;

  // The name was the only rule the old n-form carried; the dialog owns it now.
  const trimmedName = contractEditFormData.value.name.trim();
  if (!trimmedName) {
    contractNameError.value = 'Please enter a contract name';
    return;
  }
  contractNameError.value = null;

  savingContract.value = true;
  try {
    // Parse tags from comma-separated string
    const tagsArray = tagsInput.value
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);

    const updateData: ContractManagementContractUpdate = {
      name: contractEditFormData.value.name.trim(),
      contract_type_uid: contractEditFormData.value.contractTypeUid,
      description: contractEditFormData.value.description.trim() || '',
      tags: tagsArray.length > 0 ? tagsArray : null,
    };

    // Use the response directly instead of making an extra GET request
    const updatedContract = await ContractsService.update(contract.value.uid, updateData);
    contract.value = updatedContract;
    // Note: matters are not included in update response, so we keep the existing matters array
    message.success('Contract updated successfully');
    showEditContractModal.value = false;
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : 'Failed to update contract';
    message.error(errorMsg);
  } finally {
    savingContract.value = false;
  }
}

function handleCancelContractEdit(): void {
  showEditContractModal.value = false;
  contractEditFormData.value = {
    name: '',
    contractTypeUid: null,
    description: '',
    tags: [],
  };
  tagsInput.value = '';
  contractNameError.value = null;
}

async function handleDeleteContract(): Promise<void> {
  if (!contract.value) return;

  deletingContract.value = true;
  try {
    // Resolved first: once deleted, the contract has no trail to its folder.
    const landing = await contractFolderRoute('contract', contract.value.uid);
    await ContractsService.delete(contract.value.uid);
    message.success('Contract deleted');
    await router.push(landing);
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : 'Failed to delete contract';
    message.error(errorMsg);
  } finally {
    deletingContract.value = false;
  }
}

/**
 * Turn the contract back into a plain file or a folder of plain files.
 * Nobody gains or loses access; its links to parties and signing requests go.
 */
async function handleConvertContract(): Promise<void> {
  const target = pendingConversion.value;
  if (!contract.value || !target) return;
  convertingContract.value = true;
  try {
    if (target === 'file') {
      const file = await ContractsService.convertToFile(contract.value.uid);
      message.success(`“${contractName.value}” is a file again`);
      const folderUid = file.parent_directory?.uid;
      await router.push(folderUid ? vaultDirectoryRoute(folderUid) : { name: 'file-vault' });
    } else {
      const folder = await ContractsService.convertToFolder(contract.value.uid);
      message.success(`“${contractName.value}” is a folder now`);
      await router.push(vaultDirectoryRoute(folder.uid));
    }
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to convert the contract');
  } finally {
    convertingContract.value = false;
    pendingConversion.value = null;
  }
}

async function handleRepopulate(): Promise<void> {
  if (!contract.value) return;

  repopulating.value = true;
  try {
    await ContractsService.repopulate(contract.value.uid);
    message.success('Repopulation started');
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : 'Failed to start repopulation';
    message.error(errorMsg);
  } finally {
    repopulating.value = false;
  }
}

// Contract form validation rules

// ==================== Lifecycle Hooks ====================
/**
 * The contract first: everything else on the page hangs off it, so a refused
 * contract means refused files and parties too — one "not available" state
 * rather than a toast per request.
 */
async function loadContract(): Promise<void> {
  await fetchContract();
  if (unavailable.value) return;
  await Promise.all([fetchContractFiles(), fetchParties()]);
}

onMounted(loadContract);

function resetContractFilesListState(): void {
  searchQuery.value = '';
  currentPage.value = 1;
  sortKey.value = 'created_at';
  sortDirection.value = 'desc';
  sortActivated.value = false;
}

// Refetch when navigating to a different contract (same component, param change)
watch(contractUid, async (newUid, oldUid) => {
  if (newUid !== oldUid) {
    resetContractFilesListState();
    await loadContract();
  }
});
// --- chrome ----------------------------------------------------------------
const surface = gaiaNeutral.surface;
const childSurface = gaiaNeutral.surfaceChild;
const cardBorder = gaiaNeutral.borderCard;
const dividerColor = gaiaNeutral.borderDivider;
const rowBorder = gaiaNeutral.borderSoft;
const rowHover = gaiaNeutral.surfaceHover;
const borderHover = gaiaNeutral.borderHover;
const fileIconColor = gaiaNeutral.textSubtle;
const shimmerBase = gaiaNeutral.surfaceSoft;
const shimmerHighlight = gaiaNeutral.surfaceMuted;
const accentColor = gaiaAccent.base;
const monoFont = gaiaFonts.mono;
</script>

<template>
  <Layout :content-max-width="gaiaContentWidth.record">
    <template #breadcrumb>
      <n-breadcrumb-item v-if="unavailable">{{ navLabels.fileVault }}</n-breadcrumb-item>
      <template v-else>
        <gaia-vault-crumbs kind="contract" :uid="contractUid" />
        <n-breadcrumb-item>{{ contractName }}</n-breadcrumb-item>
      </template>
    </template>
    <!-- The contract is gone, or not visible from the active organisation. -->
    <gaia-not-found v-if="unavailable" />
    <n-flex v-else vertical :size="18">
      <!-- Re-extraction is a background job; this banner is the only place it
           is visible, so it stays up until the run finishes. -->
      <gaia-banner v-if="repopulating" plain type="info">
        Re-running extraction on this contract’s files. Details refresh when it finishes.
      </gaia-banner>

      <!-- Header: what this is, then the two actions worth a button of their own. -->
      <n-flex align="flex-start" :size="20" :wrap="false">
        <n-flex vertical :size="7" :style="{ minWidth: 0 }">
          <gaia-text variant="label" size="nano" tone="faint">Contract</gaia-text>
          <div v-if="loadingContract" class="shimmer title" />
          <n-flex v-else align="center" :size="12" :wrap="true">
            <gaia-header :level="1" variant="display" size="page" :style="{ margin: 0 }">
              {{ contractName }}
            </gaia-header>
            <gaia-contract-state-cell v-if="contract?.states" :states="contract.states" />
          </n-flex>
        </n-flex>

        <n-flex
          align="center"
          :size="8"
          :wrap="false"
          :style="{ marginLeft: 'auto', flex: 'none' }"
        >
          <gaia-button
            v-if="authStore.userPermissions.can_use_signing"
            type="primary"
            size="medium"
            :disabled="signBlock !== null"
            :title="signBlock ?? undefined"
            @click="handleSignThis"
          >
            <template #icon>
              <svg
                width="15"
                height="15"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M2.5 13.5h11" />
                <path
                  d="M3.8 10.8c1.4 0 1.8-6.3 3.2-6.3 1 0 .6 4.6.1 5.6-.3.6.4.9 1.1.3.8-.7 1.4-1.6 2.2-1.6.9 0 .6 1.6 1.8 1.6"
                />
              </svg>
            </template>
            Sign this
          </gaia-button>
          <gaia-dropdown
            :options="headerMenuOptions"
            placement="bottom-end"
            @select="(key: string) => onHeaderMenu(key)"
          >
            <!-- 36 to match the medium button beside it, not the 40 the CM
                 mocks use for a ⋯ that stands on its own. -->
            <gaia-icon-button :dimension="36" title="More actions">
              <n-icon :component="IconMore" :size="18" />
            </gaia-icon-button>
          </gaia-dropdown>
        </n-flex>
      </n-flex>

      <!-- Split card: the contract's own fields on the left, everything it is
           attached to on the right — two rhythms in one card, so the record
           reads as a whole rather than as two stacked panels. -->
      <div class="split-card" data-tour="cm.contract-info">
        <div class="split-left">
          <template v-if="loadingContract">
            <div v-for="index in 6" :key="index" class="field-skeleton">
              <div class="shimmer label" />
              <div class="shimmer" />
            </div>
          </template>

          <template v-else>
            <div v-for="field in detailFields" :key="field.label" class="field-row">
              <gaia-text variant="label" size="nano" tone="faint" :style="{ paddingTop: '2px' }">
                {{ field.label }}
              </gaia-text>

              <div style="min-width: 0">
                <n-flex v-if="field.kind === 'tags'" :size="5" :wrap="true">
                  <gaia-tag v-for="tag in field.tags" :key="tag" variant="muted">{{
                    tag
                  }}</gaia-tag>
                  <gaia-text v-if="!field.tags?.length" size="row" tone="blank">—</gaia-text>
                </n-flex>

                <n-flex v-else-if="field.kind === 'owner'" align="center" :size="8" :wrap="false">
                  <template v-if="contract?.owner_user">
                    <gaia-avatar :size="24">
                      {{ initialsOf(contract.owner_user.full_name || contract.owner_user.email) }}
                    </gaia-avatar>
                    <gaia-text truncate size="row" tone="text">
                      {{ contract.owner_user.full_name || contract.owner_user.email }}
                    </gaia-text>
                  </template>
                  <gaia-text v-else size="row" tone="blank">Unassigned</gaia-text>
                </n-flex>

                <gaia-text
                  v-else
                  size="row"
                  :tone="field.muted ? 'blank' : 'text'"
                  :style="{ lineHeight: 1.55 }"
                >
                  {{ field.value }}
                </gaia-text>
              </div>
            </div>
          </template>
        </div>

        <div class="split-right">
          <gaia-tab-bar
            data-tour="cm.contract-panel-tabs"
            variant="panel"
            :items="panelTabs"
            :active="rightPanelTab"
            @select="(key: string) => (rightPanelTab = key as PanelTab)"
          >
            <template v-if="panelEmptyCopy.cta" #actions>
              <gaia-icon-button
                :dimension="26"
                :title="panelAddBlock ?? panelEmptyCopy.cta"
                :disabled="panelAddBlock !== null"
                @click="onPanelAdd"
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path :d="gaiaGlyphPaths.plus" />
                </svg>
              </gaia-icon-button>
            </template>
          </gaia-tab-bar>

          <div class="panel-body">
            <template v-if="panelLoading">
              <div v-for="index in 3" :key="index" class="shimmer card" />
            </template>

            <gaia-empty-state
              v-else-if="panelIsEmpty"
              dashed
              :title="panelEmptyCopy.title"
              :style="{ padding: '30px 12px' }"
            >
              {{ panelEmptyCopy.body }}
              <template v-if="panelEmptyCopy.cta" #actions>
                <gaia-button
                  size="small"
                  :disabled="panelAddBlock !== null"
                  :title="panelAddBlock ?? undefined"
                  @click="onPanelAdd"
                  >{{ panelEmptyCopy.cta }}</gaia-button
                >
              </template>
            </gaia-empty-state>

            <template v-else-if="rightPanelTab === 'matters'">
              <div
                v-for="matter in matters"
                :key="matter.uid"
                class="panel-item"
                @click="handleMatterClick(matter.uid, $event)"
              >
                <gaia-avatar :size="26">{{ initialsOf(matter.name) }}</gaia-avatar>
                <div class="panel-item-text">
                  <gaia-text truncate size="control" weight="semibold" tone="ink">
                    <router-link
                      :to="matterRoute(matter.uid)"
                      style="color: inherit; text-decoration: none"
                      @click.stop
                    >
                      {{ matter.name }}
                    </router-link>
                  </gaia-text>
                  <gaia-text truncate size="meta" tone="subtle">
                    {{ matter.type || matter.description || 'Matter' }}
                  </gaia-text>
                </div>
                <gaia-delete-button
                  intent="remove"
                  size="xs"
                  subject="matter"
                  @click.stop="handleUnlinkMatter(matter.uid, matter.name)"
                />
              </div>
            </template>

            <template v-else-if="rightPanelTab === 'parties'">
              <div
                v-for="entry in parties"
                :key="entry.uid"
                class="panel-item"
                @click="router.push(partyRoute(entry.uid))"
              >
                <gaia-avatar :size="26">{{ initialsOf(entry.name) }}</gaia-avatar>
                <div class="panel-item-text">
                  <gaia-text truncate size="control" weight="semibold" tone="ink">
                    <router-link
                      :to="partyRoute(entry.uid)"
                      style="color: inherit; text-decoration: none"
                      @click.stop
                    >
                      {{ entry.name }}
                    </router-link>
                  </gaia-text>
                  <gaia-text truncate size="meta" tone="subtle">{{
                    PARTY_KIND_LABELS[entry.kind]
                  }}</gaia-text>
                </div>
              </div>
            </template>

            <template v-else-if="rightPanelTab === 'shared-users'">
              <div v-for="share in sharedUserList" :key="share.user.uid" class="panel-item static">
                <gaia-avatar :size="26">
                  {{ initialsOf(share.user.full_name || share.user.email) }}
                </gaia-avatar>
                <div class="panel-item-text">
                  <gaia-text truncate size="control" weight="semibold" tone="ink">
                    {{ share.user.full_name || share.user.email }}
                  </gaia-text>
                  <gaia-text truncate size="meta" tone="subtle">{{ share.user.email }}</gaia-text>
                </div>
                <gaia-tag variant="muted">{{ permissionDisplay(share.permissions) }}</gaia-tag>
                <gaia-popover
                  :show="editingShareUserUid === share.user.uid"
                  trigger="click"
                  placement="bottom-end"
                  @update:show="(value: boolean) => !value && (editingShareUserUid = null)"
                >
                  <template #trigger>
                    <gaia-icon-button
                      :dimension="24"
                      quaternary
                      :title="adminBlock ?? 'Change access'"
                      :disabled="adminBlock !== null"
                      @click.stop="openEditUserSharePopover(share)"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M3 13h3l7-7-3-3-7 7z" />
                      </svg>
                    </gaia-icon-button>
                  </template>
                  <gaia-check-menu
                    title="Access"
                    :options="editPermissionOptions"
                    :loading="savingSharePermission"
                    @toggle="toggleEditPermissionFlag"
                    @cancel="closeEditSharePopover"
                    @confirm="saveEditSharePermission"
                  />
                </gaia-popover>
                <gaia-delete-button
                  intent="remove"
                  size="xs"
                  subject="access"
                  :disabled="adminBlock !== null"
                  :title="adminBlock ?? undefined"
                  :loading="removingShareUserUid === share.user.uid"
                  @click.stop="handleRemoveSharedUser(share.user.uid)"
                />
              </div>
            </template>

            <template v-else>
              <div v-for="share in sharedRoleList" :key="share.role.uid" class="panel-item static">
                <gaia-avatar :size="26" type="mark">{{ initialsOf(share.role.name) }}</gaia-avatar>
                <div class="panel-item-text">
                  <gaia-text truncate size="control" weight="semibold" tone="ink">
                    {{ share.role.name }}
                  </gaia-text>
                  <gaia-text truncate size="meta" tone="subtle">Role</gaia-text>
                </div>
                <gaia-tag variant="muted">{{ permissionDisplay(share.permissions) }}</gaia-tag>
                <gaia-popover
                  :show="editingShareRoleUid === share.role.uid"
                  trigger="click"
                  placement="bottom-end"
                  @update:show="(value: boolean) => !value && (editingShareRoleUid = null)"
                >
                  <template #trigger>
                    <gaia-icon-button
                      :dimension="24"
                      quaternary
                      :title="adminBlock ?? 'Change access'"
                      :disabled="adminBlock !== null"
                      @click.stop="openEditRoleSharePopover(share)"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M3 13h3l7-7-3-3-7 7z" />
                      </svg>
                    </gaia-icon-button>
                  </template>
                  <gaia-check-menu
                    title="Access"
                    :options="editPermissionOptions"
                    :loading="savingSharePermission"
                    @toggle="toggleEditPermissionFlag"
                    @cancel="closeEditSharePopover"
                    @confirm="saveEditSharePermission"
                  />
                </gaia-popover>
                <gaia-delete-button
                  intent="remove"
                  size="xs"
                  subject="access"
                  :disabled="adminBlock !== null"
                  :title="adminBlock ?? undefined"
                  :loading="removingShareRoleUid === share.role.uid"
                  @click.stop="handleRemoveSharedRole(share.role.uid)"
                />
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Files -->
      <!-- No section label: the table's own header says what these rows are, and
           the count is already in the footer's range line. -->
      <n-flex align="center" :size="12" :wrap="false" :style="{ marginTop: '4px' }">
        <n-flex align="center" :size="8" :wrap="false" :style="{ marginLeft: 'auto' }">
          <gaia-search-input
            v-model:value="searchQuery"
            variant="fill"
            size="small"
            placeholder="Search files"
            :debounce="500"
            @search="applySearch"
          />
          <gaia-add-button
            subject="file"
            :disabled="writeBlock !== null"
            :title="writeBlock ?? undefined"
            @click="handleCreateClick"
          />
        </n-flex>
      </n-flex>

      <gaia-data-table
        data-tour="cm.contract-files"
        :columns="fileColumns"
        :sort-key="sortKey"
        :sort-direction="sortDirection === 'asc' ? 'asc' : 'desc'"
        :sort-active="sortActivated"
        :action-width="56"
        @sort="onFilesSort"
      >
        <template #default="{ rowStyle }">
          <template v-if="loading">
            <gaia-table-skeleton
              :row-style="rowStyle"
              :cells="['74%', '54px', '40px', '48px', '100px', '100px']"
              :rows="4"
              :row-height="54"
            />
          </template>

          <gaia-empty-state
            v-else-if="filesError"
            status="alert"
            title="Couldn’t load this contract’s files"
          >
            The contract details above loaded fine — only the file list failed. Retrying is safe.
            <template #actions>
              <gaia-button type="primary" size="small" @click="fetchContractFiles">
                Retry
              </gaia-button>
            </template>
          </gaia-empty-state>

          <gaia-empty-state
            v-else-if="contractFiles.length === 0"
            title="No files on this contract"
          >
            <template #icon>
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M4 2.5h6l4 4v9H4z" />
                <path d="M10 2.5v4h4" />
              </svg>
            </template>
            Add the signed original to make this contract reviewable, signable and exportable.
            <template #actions>
              <gaia-add-button
                subject="file"
                variant="primary"
                size="md"
                :icon="false"
                :disabled="writeBlock !== null"
                :title="writeBlock ?? undefined"
                @click="handleCreateClick"
              />
            </template>
          </gaia-empty-state>

          <template v-else>
            <template v-for="file in contractFiles" :key="file.uid">
              <gaia-table-row-state
                v-if="rowDelete.stateOf(file.uid) === 'confirm'"
                variant="confirm"
                :title="`Delete “${fileDisplayName(file)}”?`"
                @cancel="rowDelete.cancel()"
                @confirm="deleteFileConfirmed(file)"
              >
                The document is deleted with all of its versions.
                {{ totalCount === 1 ? 'It is the last file, so the contract is deleted too.' : '' }}
              </gaia-table-row-state>

              <gaia-table-row-state
                v-else-if="rowDelete.stateOf(file.uid) === 'busy'"
                variant="busy"
                note="Undo not available"
              >
                Deleting “{{ fileDisplayName(file) }}”…
              </gaia-table-row-state>

              <gaia-table-row-state
                v-else-if="rowDelete.stateOf(file.uid) === 'error'"
                variant="error"
                @retry="deleteFileConfirmed(file)"
                @dismiss="rowDelete.dismissError()"
              >
                {{ rowDelete.errorTextOf(file.uid) }}
              </gaia-table-row-state>

              <div
                v-else
                class="row contract-file-row"
                :style="rowStyle"
                @click="router.push(contractFileRoute(file))"
              >
                <div class="cell name-cell">
                  <span v-if="downloadingFileUid === file.uid" class="spinner" />
                  <svg
                    v-else
                    width="13"
                    height="13"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.4"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="file-icon"
                  >
                    <path d="M4 2.5h5l3 3v8H4z" />
                    <path d="M9 2.5v3h3" />
                  </svg>
                  <gaia-text
                    size="control"
                    weight="semibold"
                    tone="ink"
                    class="clamp"
                    :title="fileDisplayName(file)"
                  >
                    <router-link
                      :to="contractFileRoute(file)"
                      style="color: inherit; text-decoration: none"
                      @click.stop
                    >
                      {{ fileDisplayName(file) }}
                    </router-link>
                  </gaia-text>
                </div>

                <div class="cell">
                  <gaia-tag variant="muted">{{ contractFileTypeLabel(file.type) }}</gaia-tag>
                </div>

                <div class="cell">
                  <!-- Appendices take no part in the contract's state. -->
                  <gaia-contract-state-cell
                    v-if="file.vault_file?.current_revision && fileTypeHasState(file.type)"
                    :state="file.effective_state"
                    :reasons="file.needs_attention_reasons"
                    :failure-reason="file.signature_failure_reason"
                    :signature-infos="file.vault_file.current_revision.signature_infos ?? null"
                  />
                  <gaia-text v-else size="row" tone="blank">—</gaia-text>
                </div>

                <div class="cell">
                  <gaia-text size="micro" tone="muted" class="mono">{{ fileSize(file) }}</gaia-text>
                </div>

                <div class="cell">
                  <gaia-text
                    truncate
                    size="control"
                    tone="tertiary"
                    :style="{ fontVariantNumeric: 'tabular-nums' }"
                  >
                    {{ formatDate(file.created_at || null) }}
                  </gaia-text>
                </div>

                <div class="cell">
                  <gaia-text
                    truncate
                    size="control"
                    tone="tertiary"
                    :style="{ fontVariantNumeric: 'tabular-nums' }"
                  >
                    {{ formatDate(file.updated_at || null) }}
                  </gaia-text>
                </div>

                <n-flex justify="end" :size="0" :wrap="false">
                  <gaia-dropdown
                    :options="fileMenuOptions(file)"
                    placement="bottom-end"
                    @select="(key: string) => onFileMenu(key, file)"
                  >
                    <gaia-icon-button :dimension="28" quaternary title="More" @click.stop>
                      <n-icon :component="IconMore" :size="16" />
                    </gaia-icon-button>
                  </gaia-dropdown>
                </n-flex>
              </div>
            </template>
          </template>
        </template>

        <template v-if="contractFiles.length > 0 && !loading" #footer>
          <gaia-pagination
            :page="currentPage"
            :page-size="pageSize"
            :total="totalCount"
            @update:page="(page: number) => (currentPage = page)"
            @update:page-size="(size: number) => (pageSize = size)"
          />
        </template>
      </gaia-data-table>
    </n-flex>

    <!-- Header confirms act on the whole record, so they stay dialogs — there
         is no row for a strip to replace. -->
    <gaia-modal
      :show="confirmRepopulate"
      width="440px"
      :closable="false"
      content-style="padding: 20px"
      @update:show="(value: boolean) => !value && (confirmRepopulate = false)"
    >
      <gaia-text size="subtitle" weight="semibold" tone="ink">Re-run extraction?</gaia-text>
      <gaia-text
        size="body"
        tone="tertiary"
        :style="{ display: 'block', marginTop: '10px', lineHeight: 1.55 }"
      >
        This re-reads the contract’s files and overwrites its tags, description and other extracted
        details with what it finds. It runs in the background.
      </gaia-text>
      <n-flex justify="end" :size="8" :style="{ marginTop: '20px' }">
        <gaia-button size="small" @click="confirmRepopulate = false">Cancel</gaia-button>
        <gaia-button
          type="primary"
          size="small"
          :loading="repopulating"
          @click="confirmRepopulateNow"
        >
          Re-run extraction
        </gaia-button>
      </n-flex>
    </gaia-modal>

    <gaia-modal
      :show="confirmDeleteContract"
      width="440px"
      :closable="false"
      content-style="padding: 20px"
      @update:show="(value: boolean) => !value && (confirmDeleteContract = false)"
    >
      <gaia-text size="subtitle" weight="semibold" tone="ink">
        Delete “{{ contractName }}”?
      </gaia-text>
      <gaia-text
        size="body"
        tone="tertiary"
        :style="{ display: 'block', marginTop: '10px', lineHeight: 1.55 }"
      >
        The contract and all of its files are deleted, with every version. This cannot be undone.
      </gaia-text>
      <n-flex justify="end" :size="8" :style="{ marginTop: '20px' }">
        <gaia-button size="small" @click="confirmDeleteContract = false">Cancel</gaia-button>
        <gaia-button
          type="error"
          size="small"
          :loading="deletingContract"
          @click="confirmDeleteNow"
        >
          Delete contract
        </gaia-button>
      </n-flex>
    </gaia-modal>

    <gaia-modal
      :show="pendingConversion !== null"
      width="440px"
      :closable="false"
      content-style="padding: 20px"
      @update:show="(value: boolean) => !value && (pendingConversion = null)"
    >
      <gaia-text size="subtitle" weight="semibold" tone="ink">
        Convert “{{ contractName }}” to a {{ pendingConversion === 'file' ? 'file' : 'folder' }}?
      </gaia-text>
      <gaia-text
        size="body"
        tone="tertiary"
        :style="{ display: 'block', marginTop: '10px', lineHeight: 1.55 }"
      >
        {{
          pendingConversion === 'file'
            ? 'Its one document becomes a plain file in the same folder.'
            : 'A folder named after the contract takes its place, holding its documents as plain files.'
        }}
        The contract goes, with its links to parties and signing requests. Who can access the
        documents does not change.
      </gaia-text>
      <n-flex justify="end" :size="8" :style="{ marginTop: '20px' }">
        <gaia-button size="small" @click="pendingConversion = null">Cancel</gaia-button>
        <gaia-button
          type="primary"
          size="small"
          :loading="convertingContract"
          @click="handleConvertContract"
        >
          Convert
        </gaia-button>
      </n-flex>
    </gaia-modal>

    <ContractFileCreateModal
      v-model:show="showCreateModal"
      :contract-uid="contractUid"
      @created="handleContractFileCreated"
    />

    <!-- Move Contract File Modal -->
    <MoveContractFileModal
      v-if="contractFileToMoveUid"
      v-model:show="showMoveModal"
      :contract-file-uid="contractFileToMoveUid"
      @moved="handleMoved"
    />

    <!-- Add Matter Modal -->
    <SelectEntityModal
      v-model:show="showAddMatterModal"
      entity-type="matter"
      :contract-uid="contractUid"
      @assigned="handleMatterAssigned"
    />

    <!-- Share Contract Modal -->
    <mass-share-contracts-modal
      v-model:show="showShareModal"
      :contract-uids="contractUid ? [contractUid] : []"
      :contract-names="contract?.name ? [contract.name] : []"
      @shared="handleSharedFromModal"
    />

    <!-- Edit contract file -->
    <gaia-modal
      :show="showEditModal"
      width="460px"
      :closable="false"
      content-style="padding: 20px"
      @update:show="(value: boolean) => !value && handleCancelEdit()"
    >
      <gaia-text size="subtitle" weight="semibold" tone="ink">Edit contract file</gaia-text>

      <n-flex vertical :size="14" :style="{ marginTop: '16px' }">
        <gaia-form-field label="File type" required>
          <gaia-select
            :value="editFormData.type"
            :options="fileTypeOptions"
            placeholder="Not classified yet"
            style="width: 100%"
            @update:value="
              (value: ContractManagementContractFileType) => (editFormData.type = value)
            "
          />
        </gaia-form-field>

        <gaia-form-field label="Description" hint="Optional">
          <gaia-textarea
            :value="editFormData.description"
            :autosize="{ minRows: 3 }"
            style="width: 100%"
            @update:value="(value: string) => (editFormData.description = value)"
          />
        </gaia-form-field>
      </n-flex>

      <n-flex justify="end" :size="8" :style="{ marginTop: '18px' }">
        <gaia-button size="small" @click="handleCancelEdit">Cancel</gaia-button>
        <gaia-button type="primary" size="small" :loading="saving" @click="handleSaveEdit">
          {{ saving ? 'Saving…' : 'Save changes' }}
        </gaia-button>
      </n-flex>
    </gaia-modal>

    <!-- Edit contract -->
    <gaia-modal
      :show="showEditContractModal"
      width="520px"
      :closable="false"
      content-style="padding: 20px"
      @update:show="(value: boolean) => !value && handleCancelContractEdit()"
    >
      <gaia-text size="subtitle" weight="semibold" tone="ink">Edit contract</gaia-text>

      <n-flex vertical :size="14" :style="{ marginTop: '16px' }">
        <gaia-form-field label="Name" required :error="contractNameError || undefined">
          <gaia-input
            :value="contractEditFormData.name"
            size="medium"
            :maxlength="255"
            :status="contractNameError ? 'error' : undefined"
            style="width: 100%"
            @update:value="
              (value: string) => {
                contractEditFormData.name = value;
                contractNameError = null;
              }
            "
          />
        </gaia-form-field>

        <gaia-form-field label="Type">
          <gaia-select
            :value="contractEditFormData.contractTypeUid"
            :options="contractTypeStore.uidOptions"
            placeholder="Select contract type"
            clearable
            filterable
            style="width: 100%"
            @update:value="(value: string | null) => (contractEditFormData.contractTypeUid = value)"
          />
        </gaia-form-field>

        <gaia-form-field label="Tags" hint="Comma separated">
          <gaia-input
            :value="tagsInput"
            size="medium"
            placeholder="procurement, vendor-agreement"
            style="width: 100%"
            @update:value="(value: string) => (tagsInput = value)"
          />
        </gaia-form-field>

        <gaia-form-field label="Description" hint="Optional">
          <gaia-textarea
            :value="contractEditFormData.description"
            :autosize="{ minRows: 3 }"
            style="width: 100%"
            @update:value="(value: string) => (contractEditFormData.description = value)"
          />
        </gaia-form-field>
      </n-flex>

      <n-flex justify="end" :size="8" :style="{ marginTop: '18px' }">
        <gaia-button size="small" @click="handleCancelContractEdit">Cancel</gaia-button>
        <gaia-button
          type="primary"
          size="small"
          :loading="savingContract"
          @click="handleSaveContractEdit"
        >
          {{ savingContract ? 'Saving…' : 'Save changes' }}
        </gaia-button>
      </n-flex>
    </gaia-modal>

    <VaultFileRevisionUploadModal
      v-if="revisionUploadFileUid"
      v-model:show="showRevisionUploadModal"
      :file-uid="revisionUploadFileUid"
      :file-name="revisionUploadFileName"
      @uploaded="fetchContractFiles"
    />

    <NModal
      v-model:show="showGaiaSectionModal"
      preset="card"
      style="width: 500px"
      title="Export to Gaia CLM Section"
      @after-leave="closeGaiaSectionModal"
    >
      <div class="gaia-section-modal-content">
        <p>Select the Gaia sections where you want to export this contract file:</p>

        <gaia-select
          v-model:value="selectedGaiaSections"
          :options="gaiaSectionOptions"
          placeholder="Select sections..."
          multiple
          filterable
          :loading="gaiaStore.sectionGroups.loading"
          style="margin-bottom: 16px"
        />

        <div v-if="gaiaExportError" class="gaia-export-error">
          {{ gaiaExportError }}
        </div>

        <div v-if="selectedGaiaSections.length > 0" class="selected-sections-info">
          <p>
            <strong>{{ gaiaExportFileName }}</strong> will be exported to
            <strong>{{ selectedGaiaSections.length }}</strong> section(s)
          </p>
        </div>
      </div>

      <template #footer>
        <NSpace justify="end">
          <NButton @click="closeGaiaSectionModal">Cancel</NButton>
          <NButton
            type="primary"
            :loading="isExportingToGaia"
            :disabled="selectedGaiaSections.length === 0"
            @click="exportContractFileToGaiaSections"
          >
            Export File
          </NButton>
        </NSpace>
      </template>
    </NModal>

    <!-- Flush: the preview sizes itself to the drawer body and scrolls its own pages. -->
    <gaia-drawer
      v-model:show="showPreview"
      :width="640"
      title="File preview"
      flush
      @after-leave="previewFileUid = null"
    >
      <gaia-vault-file-pdf-preview v-if="previewFileUid" :vault-file-uid="previewFileUid" />
    </gaia-drawer>
  </Layout>
</template>

<style scoped>
/* The split card: contract fields left, attachments right. It wraps rather than
   scrolls — below the combined minimum the right panel drops under the left. */
.split-card {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  overflow: hidden;
  border: 1px solid v-bind(cardBorder);
  border-radius: 12px;
  background: v-bind(surface);
}

.split-left {
  flex: 1 1 440px;
  min-width: 420px;
  padding: 20px 22px;
}

.split-right {
  display: flex;
  flex: 1 1 392px;
  flex-direction: column;
  min-width: 340px;
  border-left: 1px solid v-bind(dividerColor);
  background: v-bind(childSurface);
}

/* A definition row: fixed label column, value takes the rest. */
.field-row {
  display: grid;
  grid-template-columns: 112px minmax(200px, 1fr);
  gap: 20px;
  align-items: start;
  padding: 11px 0;
  border-bottom: 1px solid v-bind(rowBorder);
}

.field-row:last-child {
  border-bottom: none;
}

.field-skeleton {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 11px 0;
}

/* The panel/title shimmers — the table body's shimmer is GaiaTableSkeleton,
   but the field rows, title and cards above it keep their own bars. */
.shimmer {
  flex: 1;
  height: 11px;
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    v-bind(shimmerHighlight) 0%,
    v-bind(shimmerBase) 45%,
    v-bind(shimmerHighlight) 90%
  );
  background-size: 480px 100%;
  animation: detail-shimmer 1.5s linear infinite;
}

.shimmer.title {
  flex: none;
  width: 460px;
  height: 26px;
  border-radius: 6px;
}

.shimmer.label {
  flex: none;
  width: 88px;
  height: 9px;
}

.shimmer.card {
  flex: none;
  height: 56px;
  border-radius: 10px;
}

.panel-body {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 8px;
  min-height: 236px;
  padding: 12px 14px;
}

/* An attachment card — a matter, a shared user, a shared role. */
.panel-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid v-bind(cardBorder);
  border-radius: 10px;
  background: v-bind(surface);
  cursor: pointer;
}

.panel-item:hover {
  border-color: v-bind(borderHover);
}

.panel-item.static {
  cursor: default;
}

/* The text takes the slack so the permission chip and the two controls sit
   hard right, in the same place on every row — otherwise they float wherever
   the name happens to end and the edit/remove targets move row to row. */
.panel-item-text {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

/* Remove is the one destructive control in the card, so it only turns alert on
   hover rather than sitting there red. */

.row {
  display: grid;
  align-items: center;
  gap: 14px;
  min-height: 54px;
  padding: 0 18px;
  border-bottom: 1px solid v-bind(rowBorder);
  cursor: pointer;
}

.row:hover {
  background: v-bind(rowHover);
}

.cell {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.name-cell {
  align-items: flex-start;
  gap: 9px;
  padding: 9px 0;
}

.file-icon {
  flex: none;
  margin-top: 2px;
  color: v-bind(fileIconColor);
}

.clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-width: 0;
  line-height: 1.45;
}

/* Sizes are compared down the column, so they take the mono face. */
.mono {
  font-family: v-bind(monoFont);
}

.spinner {
  flex: none;
  width: 13px;
  height: 13px;
  margin-top: 2px;
  border: 1.6px solid v-bind(accentColor);
  border-right-color: transparent;
  border-radius: 50%;
  animation: detail-spin 800ms linear infinite;
}

@keyframes detail-shimmer {
  0% {
    background-position: -280px 0;
  }

  100% {
    background-position: 480px 0;
  }
}
</style>
